import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  addNoteDocument,
  getNoteDocument,
  listNoteDocuments,
  removeNoteDocument,
  type ReportDocument,
} from './api-parity';
import { MAX_NOTE_FILES, prepareFiles, type PendingFile, type RefusedFile } from './note-files';

export type NoteFilesList =
  | { readonly status: 'loading' | 'failed' }
  | { readonly status: 'ready'; readonly items: ReportDocument[]; readonly canAdd: boolean };

/** The note's own files as the server lists them, read again when `key` changes. */
export function useNoteFilesList(reportId: string | undefined, key: string) {
  const [list, setList] = useState<NoteFilesList>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (reportId === undefined) return;
    let live = true;
    void listNoteDocuments(reportId).then((out) => {
      if (!live) return;
      setList(
        out.ok
          ? {
              status: 'ready',
              // A view without the lists (an older finance) reads as no file, none to add.
              items: Array.isArray(out.value.items) ? out.value.items : [],
              canAdd: out.value.can?.add === true,
            }
          : { status: 'failed' },
      );
    });
    return () => {
      live = false;
    };
  }, [reportId, key, attempt]);
  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  return { list, reload } as const;
}

/** Opens a file in a new tab: the tab opens at the click, the blob follows (as the note's PDF). */
export async function openNoteFile(reportId: string, documentId: string): Promise<boolean> {
  const tab = window.open('', '_blank');
  if (tab === null) return false;
  const out = await getNoteDocument(reportId, documentId);
  if (!out.ok) {
    tab.close();
    return false;
  }
  const url = URL.createObjectURL(out.value);
  tab.location.href = url;
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return true;
}

export type PanelError = { readonly name: string; readonly code: string };

/** Files chosen and not sent yet, with their labels, and the files refused on the way in. */
export function usePendingFiles(room: number) {
  const { t } = useTranslation('expenses-parity');
  const [pending, setPending] = useState<PendingFile[]>([]);
  const [refused, setRefused] = useState<RefusedFile[]>([]);
  const [preparing, setPreparing] = useState(false);

  const add = async (files: File[]): Promise<void> => {
    setPreparing(true);
    const prepared = await prepareFiles(files, room - pending.length, (pages) =>
      t('files.photos', { count: pages }),
    );
    setPending((current) => [...current, ...prepared.accepted]);
    setRefused((current) => [...current, ...prepared.refused]);
    setPreparing(false);
  };
  const label = (key: string, text: string): void =>
    setPending((current) => current.map((f) => (f.key === key ? { ...f, label: text } : f)));
  const drop = (key: string): void => setPending((current) => current.filter((f) => f.key !== key));
  const dismiss = (key: string): void =>
    setRefused((current) => current.filter((f) => f.key !== key));
  /** Keeps only the files named, the ones a save could not send. */
  const keep = (keys: readonly string[]): void =>
    setPending((current) => current.filter((f) => keys.includes(f.key)));
  const clear = (): void => {
    setPending([]);
    setRefused([]);
  };
  return { pending, refused, preparing, add, label, drop, dismiss, keep, clear } as const;
}

/**
 * The note page's files (board 1.4, 1.6): chosen files wait with their labels for «Carica»; each
 * ✕ on a saved file removes it at once. Gestione adds in any status; who may remove a file is the
 * server's per-file `can.remove`.
 */
export function useNoteFilesPanel(reportId: string, key: string, onMoved: () => void) {
  const { list, reload } = useNoteFilesList(reportId, key);
  const held = list.status === 'ready' ? list.items.length : 0;
  const files = usePendingFiles(MAX_NOTE_FILES - held);
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState<PanelError[]>([]);

  const send = async (): Promise<void> => {
    setSending(true);
    setErrors([]);
    const failed: PanelError[] = [];
    const kept: string[] = [];
    for (const file of files.pending) {
      const out = await addNoteDocument(reportId, file.pdf, file.source, file.label);
      if (!out.ok) {
        failed.push({ name: file.name, code: out.code });
        kept.push(file.key);
      }
    }
    files.keep(kept);
    setErrors(failed);
    setSending(false);
    reload();
    if (failed.length < files.pending.length) onMoved();
  };

  const remove = async (file: ReportDocument): Promise<void> => {
    setErrors([]);
    const out = await removeNoteDocument(reportId, file.documentId);
    if (!out.ok) setErrors([{ name: file.label ?? file.documentId, code: out.code }]);
    reload();
    if (out.ok) onMoved();
  };

  return { list, files, sending, errors, send, remove } as const;
}
